import { Search, MailIcon, MessageCircle, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { FilterPill } from '@/components/common/FilterPill';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { Pagination } from '@/components/common/Pagination';
import { useNotificationLog } from '@/data/api';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { NotificationLogEntry } from '@/types/request';

const CHANNEL_ICON = {
  PUSH: <Smartphone className="h-3.5 w-3.5" />,
  SMS: <MessageCircle className="h-3.5 w-3.5" />,
  EMAIL: <MailIcon className="h-3.5 w-3.5" />,
};

const STATUS_COLOR: Record<NotificationLogEntry['status'], string> = {
  DELIVERED: 'bg-status-success-bg text-status-success',
  FAILED: 'bg-status-error-bg text-status-error',
  PENDING: 'bg-status-warning-bg text-status-warning-text',
};

const PAGE_SIZE = 20;

export function NotificationsLogPage() {
  const [search, setSearch] = useState('');
  const [channel, setChannel] = useState<NotificationLogEntry['channel'] | null>(null);
  const [page, setPage] = useState(0);

  // Reset to page 0 when filters change so we don't land on a page that
  // no longer exists in the smaller result set.
  useEffect(() => {
    setPage(0);
  }, [search, channel]);

  const listQuery = useNotificationLog({
    search,
    channel: channel ?? undefined,
    page,
    size: PAGE_SIZE,
  });
  const rows = listQuery.data?.content ?? [];
  const totalElements = listQuery.data?.totalElements ?? 0;
  const totalPages = listQuery.data?.totalPages ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <Card padding="sm" className="flex flex-wrap items-center gap-2.5">
        <Input
          containerClassName="w-72"
          placeholder="Search body, mobile, email, account"
          iconLeft={<Search className="h-4 w-4" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {(['PUSH', 'SMS', 'EMAIL'] as const).map((c) => (
          <FilterPill
            key={c}
            label={c.charAt(0) + c.slice(1).toLowerCase()}
            // Channel is single-select on the server side; clicking the same
            // pill again clears it.
            active={channel === c}
            onClick={() => setChannel((prev) => (prev === c ? null : c))}
            onClear={channel === c ? () => setChannel(null) : undefined}
          />
        ))}
      </Card>

      <Card padding="none" className="overflow-hidden">
        {listQuery.isLoading ? (
          <div className="p-4 space-y-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : listQuery.isError ? (
          <ErrorState
            title="Couldn't load notifications"
            description={(listQuery.error as Error)?.message ?? 'Try again.'}
            onRetry={() => listQuery.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No notifications match your filters"
            description="Adjust the search or channel filter."
          />
        ) : (
          <table className="w-full text-body">
            <thead className="border-b border-line">
              <tr className="text-overline text-ink-hint uppercase">
                <th className="text-left px-4 py-2.5">Sent at</th>
                <th className="text-left py-2.5">Recipient</th>
                <th className="text-left py-2.5">Channel</th>
                <th className="text-left py-2.5">Title</th>
                <th className="text-left py-2.5">Body</th>
                <th className="text-left px-4 py-2.5">Delivery</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((n) => (
                <tr key={n.id} className="border-t border-line-soft hover:bg-canvas-hover">
                  <td className="px-4 py-2.5 text-ink-muted whitespace-nowrap">
                    {formatDateTime(n.sentAt)}
                  </td>
                  <td className="py-2.5 font-mono text-caption">{n.recipient}</td>
                  <td className="py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-caption text-ink-muted">
                      {CHANNEL_ICON[n.channel]}
                      {n.channel}
                    </span>
                  </td>
                  <td className="py-2.5">{n.title}</td>
                  <td className="py-2.5 text-ink-muted truncate max-w-[400px]">
                    {n.bodyPreview}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-pill px-2.5 py-0.5 text-overline uppercase',
                        STATUS_COLOR[n.status],
                      )}
                    >
                      {n.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!listQuery.isLoading && !listQuery.isError && totalElements > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </Card>
    </div>
  );
}
